import {
  bool,
  func,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from "prop-types";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { AnimatePresence, motion } from "framer-motion";

import FnrHelpInvitation from "UI/FnrHelpInvitation/FnrHelpInvitation";
import RunningUno from "UI/RunningUno";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";
import { heightMotion } from "UI/motionStyles";

import { ensureLeadingHex } from "Utilities/Colors/colorUtils";
import useIsUnloading from "Utilities/Hooks/useIsUnloading";
import useIsWiderThan from "Utilities/Hooks/useIsWiderThan";
import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";
import useUrlParams from "Utilities/Hooks/useUrlParams";

import { usePostTwoFactorAuth } from "./API/queries";
import { use2FAState, useClearErrorOnChange } from "./API/queryHooks";
import Header from "./components/Header";
import ComponentSwitch from "./components/screens/ComponentSwitch";
import { USE_LOGIN_PASSWORD, variants } from "./constants";
import { useInitLocalState } from "./stateHooks";
import useIsLoginActive from "./useIsLoginActive";

const TwoFAContext = createContext();
const defaultObject = {};

/**
 * @returns {TwoFAContext}
 */
const useTwoFAContext = () => useContext(TwoFAContext) || defaultObject;

const MaxWidth = styled(motion.div)`
  width: ${({ $padding, $width }) =>
    `min(${$width}, calc(95vw - ${$padding * 2}px))`};
  padding: ${({ $padding }) => `${$padding}px;`};
`;

const HeightMotionRelay = ({ children, show, active }) => {
  if (active)
    return (
      <AnimatePresence>
        {show && <motion.div {...heightMotion}>{children}</motion.div>}
      </AnimatePresence>
    );

  return show ? children : null;
};
HeightMotionRelay.propTypes = { children: node, show: bool, active: bool };

const TwoFAContent = () => {
  useInitLocalState();
  const {
    variant,
    padding,
    width,
    animateEntranceHeight,
    containerRef,
    showHelpEmail,
  } = useTwoFAContext();
  const [{ networkName }] = use2FAState();
  useClearErrorOnChange({ variant });

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const [, setIsLoginActive] = useIsLoginActive();
  useEffect(() => {
    // we use the timeout here so that if another TwoFA instance is closing, that animation can complete and fire cleanup before setting isActiveLogin back to true.
    setTimeout(() => setIsLoginActive(true), 300);
    return () => setIsLoginActive(false);
  }, [setIsLoginActive]);

  return (
    <MaxWidth
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={animateEntranceHeight ? { delay: 0.3 } : {}}
      $variant={variant}
      $padding={padding}
      $width={width}
    >
      <HeightMotionRelay
        show={Boolean(containerRef.current && isMounted)}
        active={animateEntranceHeight}
      >
        <Header />
        <FnrVerticalGap gap={40}>
          <ComponentSwitch />
          {showHelpEmail && (
            <FnrHelpInvitation showLogo={Boolean(networkName)} />
          )}
        </FnrVerticalGap>
      </HeightMotionRelay>
    </MaxWidth>
  );
};

/**
 *
 * @param {object} param0
 * @param {LoginVariant} param0.variant
 * @param {function} param0.skipAuth - the function to be executed if the user skips authentication. Button is hidden when undefined.
 * @param {boolean} param0.noSkipAuthMessage - if skipAuth is defined, this will make the skip button disabled and display the given string.
 * @param {function} param0.onSuccess - a function to be executed once authentication succeeds
 * @param {function} param0.onInputChange - a function to be executed any time an input changes
 * @param {string} param0.tabID - a uuid to be passed with each call to the endpoint
 * @param {string|boolean} param0.headerText - pass nothing for default header, a string for a custom header, or false to omit from UI
 * @param {boolean} param0.showNetwork - defaults to true. pass false to hide network details.
 * @param {string} param0.emailPhone - prepopulates first factor. Overridden by url param (?emailPhone=test@test.com).
 * @param {string} param0.accentColor - the color to be used for icons, etc. defaults to brandFnBlue
 * @param {number} param0.padding - the amount of padding to be applied. Defaults to 0. Is used to determine with of component in narrow contexts.
 * @param {string} param0.width - the width of the component when unrestricted by outer context
 * @param {boolean} param0.isDark - set to true when setting this component on a dark background.
 * @param {boolean} param0.animateEntranceHeight - set to true to animate the height after initial load.
 * @param {boolean} param0.showHelpEmail - set to true to display FnrHelpInvitation at the bottom.
 * @param {boolean} param0.stoutInputs - uses the stout version of fnrInput2.
 * @param {boolean} param0.successSplash - adds a success spash before redirecting.
 * @returns
 */
const TwoFA = ({
  variant = variants.login,
  skipAuth,
  noSkipAuthMessage,
  onSuccess,
  onInputChange,
  tabID,
  headerText,
  showNetwork = true,
  emailPhone: emailPhoneArg,
  accentColor: accentColorArg,
  padding = 0,
  width = "508px",
  minHeight = "0px",
  isDark,
  animateEntranceHeight,
  showHelpEmail,
  stoutInputs,
  successSplash,
  noRedirect,
  remoteCancelRef,
}) => {
  const { emailPhone = emailPhoneArg } = useUrlParams();
  const [storedUsePassword] = useLocalStorage(USE_LOGIN_PASSWORD);
  const isUnloading = useIsUnloading();

  const { isInitialLoading } = usePostTwoFactorAuth({ variant });
  const [firstFactor, setFirstFactor] = useState(emailPhone || "");

  const [firstCode, setFirstCode] = useState("");
  const [secondCode, setSecondCode] = useState("");
  const [networkID, setNetworkID] = useState();
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(storedUsePassword);
  const [fullName, setFullName] = useState("");
  const containerRef = useRef(null);
  const isWideView = useIsWiderThan(containerRef, { px: 380 });

  const accentColor = accentColorArg
    ? ensureLeadingHex(accentColorArg)
    : undefined;

  return (
    <>
      {/* The initial loading should happen very very quickly. */}
      <HeightMotionRelay show={isInitialLoading} active={animateEntranceHeight}>
        <RunningUno color="white" width={width} />
      </HeightMotionRelay>
      <TwoFAContext.Provider
        value={{
          firstFactor,
          setFirstFactor,
          firstCode,
          setFirstCode,
          secondCode,
          setSecondCode,
          networkID,
          setNetworkID,
          password,
          setPassword,
          usePassword,
          setUsePassword,
          fullName,
          setFullName,
          // neatly prop drill props into context
          variant,
          skipAuth,
          noSkipAuthMessage,
          onSuccess,
          onInputChange,
          tabID,
          headerText,
          showNetwork,
          accentColor,
          padding,
          width,
          minHeight,
          isDark,
          animateEntranceHeight,
          containerRef,
          isWideView,
          showHelpEmail,
          stoutInputs,
          successSplash,
          noRedirect,
          remoteCancelRef,
        }}
      >
        <AnimatePresence>
          {!isInitialLoading && !isUnloading && <TwoFAContent />}
        </AnimatePresence>
      </TwoFAContext.Provider>
    </>
  );
};

TwoFA.propTypes = {
  variant: oneOf(Object.keys(variants)),
  skipAuth: func,
  noSkipAuthMessage: oneOfType([string, bool]),
  onSuccess: func,
  onInputChange: func,
  tabID: string,
  headerText: oneOfType([string, bool]),
  showNetwork: bool,
  emailPhone: string,
  accentColor: string,
  padding: number,
  width: string,
  minHeight: string,
  isDark: bool,
  animateEntranceHeight: bool,
  showHelpEmail: bool,
  stoutInputs: bool,
  successSplash: bool,
  noRedirect: bool,
  remoteCancelRef: object,
};

export { useTwoFAContext };
export default TwoFA;
